// 撮影画像のピントの鋭さ（ピンぼけの度合い）を数値にする。
//
// 姉妹アプリ food-label-assist（v5.0.17）と同じ計測（このファイルの計測部分は FLA の
// src/utils/imageSharpness.js と同一に保つ。tests/imageSharpness.test.mjs が照合する）。
// 背景: ピンぼけで文字が読めないラベルでも、AI は「読めない」と返さずに、もっともらしい
// 内容を作ったり、一部を読み落としたりする（FLA の綿半パートナーズの検査 30 件中 6 件）。
// AI に頼らない画像側の指標で「読める写真か」を測り、エッジの数がしきい値
// （api/_imageQuality.js）を下回る画像があれば、判定の横に「画像不鮮明」を表示する。
// 判定（SAFE/DANGER）そのものは変えない。
//
// 指標は性質の違う 3 種類を並べて記録する（届いた実画像 13 枚での試算では、どれか 1 つで
// 「読める裏面」と「ぼけた裏面」を分けきれなかったため）:
//
// 1. エッジの数（edgeCount / edgePerMpx）: 明るさの差が大きく、かつ急な輪郭の数。文字がくっきり
//    写っていれば多く、ぼけると減る。試算で最もよく分かれた（ぼけ 2,900〜4,500 / 読める 9,500〜12,000）。
//    ただし写っている文字の量にも比例するため、ラベルが小さく写っただけでも減る。
// 2. エッジの幅（edgeWidthP50 / edgeWidthMean）: 輪郭が何ピクセルにわたってにじんでいるか。
//    文字の大きさやコントラストに左右されにくい。ひどいぼけ（#5: 10px）ははっきり出るが、
//    軽いぼけ（5〜7px）は読める裏面（4〜6px）と重なった。
// 3. ラプラシアン分散（p50/p90/p98、labelP50/labelP90）: 隣り合う画素との明るさの差のばらつき。
//    一般的な指標だが、文字の大きさとコントラストで値が大きく変わる。label* は「白っぽい地に
//    暗い文字が点在する」マス（印字されたラベルらしいマス）だけの集計で、背景の蛍光灯や床の
//    輪郭に引っぱられないようにするためのもの（明るさ 250 以上が多いマス＝光源・白飛びは除く）。
// 4. 文字の大きさに対するにじみ（labelEdgeWidthMean / labelPitchP50 / labelEdgeRel、2026-10-11 追加）:
//    ラベルらしいマスの中だけで、輪郭のにじみの幅と、文字の画の間隔（横に走査したときの文字の画素の
//    かたまりの始まりどうしの距離。文字が大きく写るほど広い）を測り、にじみの幅を画の間隔で割る。
//    1〜3 は文字の大きさ・撮影距離で値が変わり、1 枚の絶対値で「ぼけている」と言い切れなかった
//    （2026-10-11、匠の膳 #1768 のラベル部分の鮮明さは 10/1 以降の 496 枚の中央値より上だった。
//    food-label-assist の docs/session-20261011.md）。にじみを文字の大きさと比べれば、小さい文字がにじんだ写真
//    （読めない写真）を、大きい文字の写真と同じ尺度で比べられる見込み。記録だけで、判定には使わない
//    （記録が溜まってから基準を決める）。既存の項目の定義は変えていないので版は 1 のまま

export const SHARPNESS_VERSION = 1;
export const TILE = 64;

// ラベルらしいマスの条件（初期値。記録を見て見直す）
const LABEL_MIN_MEAN = 140;       // 地が白っぽい
const LABEL_MAX_MEAN = 248;
const LABEL_INK_DELTA = 40;       // マスの平均より 40 以上暗い画素を「文字」とみなす
const LABEL_MIN_INK = 0.02;       // 文字の画素が 2〜40%
const LABEL_MAX_INK = 0.40;
const LABEL_MAX_SATURATED = 0.25; // 明るさ 250 以上（白飛び・光源）が 25% を超えるマスは除く

// エッジの条件
const EDGE_MIN_GRADIENT = 20;     // 2 画素離れた明るさの差がこれ以上の位置を輪郭の候補にする
const EDGE_MIN_CONTRAST = 40;     // 輪郭の両端の明るさの差がこれ未満なら数えない（ノイズ・地の模様）
const EDGE_ROW_STEP = 2;          // 横方向の走査を 1 行おきにして計算量を半分にする

const quantile = (sorted, p) => (sorted.length === 0 ? null : sorted[Math.floor((sorted.length - 1) * p)]);
const round = (v) => (v == null ? null : Math.round(v));
const round2 = (v) => (v == null ? null : Math.round(v * 100) / 100);

// ラベルらしいマスの中で、横に走査して文字の画素（マスの平均より PITCH_INK_DELTA 以上暗い）のかたまりを探し、
// かたまりの始まりどうしの距離（画の間隔）を集める。マスの端をまたぐ距離は数えない。ぼけると文字の画素が
// 薄くなり、ラベルらしいマスの条件（LABEL_INK_DELTA）では画が見つからなくなるため、浅い差で数える
const PITCH_INK_DELTA = 10;
function inkPitches(gray, width, tx, ty, mean, out) {
  for (let y = ty; y < ty + TILE; y += EDGE_ROW_STEP) {
    const row = y * width;
    let prevStart = -1;
    let inInk = false;
    for (let x = tx; x < tx + TILE; x++) {
      const ink = gray[row + x] < mean - PITCH_INK_DELTA;
      if (ink && !inInk) {
        if (prevStart >= 0) out.push(x - prevStart);
        prevStart = x;
      }
      inInk = ink;
    }
  }
}

function laplacianStats(gray, width, height) {
  const all = [];
  const label = [];
  const pitches = [];
  // ラベルらしいマスの位置（edgeStats でマスの中の輪郭だけを集めるのに使う）
  const tilesX = Math.floor(width / TILE);
  const labelMask = new Uint8Array(tilesX * Math.floor(height / TILE));
  for (let ty = 0; ty + TILE <= height; ty += TILE) {
    for (let tx = 0; tx + TILE <= width; tx += TILE) {
      let sum = 0;
      let saturated = 0;
      for (let y = ty; y < ty + TILE; y++) {
        for (let x = tx; x < tx + TILE; x++) {
          const v = gray[y * width + x];
          sum += v;
          if (v >= 250) saturated++;
        }
      }
      const mean = sum / (TILE * TILE);
      let ink = 0;
      let s = 0;
      let s2 = 0;
      let n = 0;
      for (let y = ty + 1; y < ty + TILE - 1; y++) {
        for (let x = tx + 1; x < tx + TILE - 1; x++) {
          const i = y * width + x;
          if (gray[i] < mean - LABEL_INK_DELTA) ink++;
          const lap = 4 * gray[i] - gray[i - 1] - gray[i + 1] - gray[i - width] - gray[i + width];
          s += lap;
          s2 += lap * lap;
          n++;
        }
      }
      const m = s / n;
      const variance = s2 / n - m * m;
      all.push(variance);
      const inkRatio = ink / n;
      const saturatedRatio = saturated / (TILE * TILE);
      if (mean >= LABEL_MIN_MEAN && mean <= LABEL_MAX_MEAN
        && inkRatio >= LABEL_MIN_INK && inkRatio <= LABEL_MAX_INK
        && saturatedRatio <= LABEL_MAX_SATURATED) {
        label.push(variance);
        labelMask[(ty / TILE) * tilesX + tx / TILE] = 1;
        inkPitches(gray, width, tx, ty, mean, pitches);
      }
    }
  }
  all.sort((a, b) => a - b);
  label.sort((a, b) => a - b);
  pitches.sort((a, b) => a - b);
  return {
    labelMask,
    tilesX,
    labelPitchP50: quantile(pitches, 0.5),
    tiles: all.length,
    p50: round(quantile(all, 0.5)),
    p90: round(quantile(all, 0.9)),
    p98: round(quantile(all, 0.98)),
    labelTiles: label.length,
    labelP50: round(quantile(label, 0.5)),
    labelP90: round(quantile(label, 0.9)),
  };
}

// 横方向に走査し、明るさの勾配が極大になる位置を輪郭とみなす。輪郭の左右へ、明るさが同じ向きに
// 変化し続ける範囲を辿り、その幅を「エッジの幅」とする（Marziliano らのエッジ幅の考え方）
// labelMask があれば、ラベルらしいマスの中の輪郭の幅も別に集める
function edgeStats(gray, width, height, labelMask = null, tilesX = 0) {
  const widths = [];
  const labelWidths = [];
  for (let y = 2; y < height - 2; y += EDGE_ROW_STEP) {
    const row = y * width;
    for (let x = 2; x < width - 2; x++) {
      const g = gray[row + x + 1] - gray[row + x - 1];
      const ag = Math.abs(g);
      if (ag < EDGE_MIN_GRADIENT) continue;
      const gl = Math.abs(gray[row + x] - gray[row + x - 2]);
      const gr = Math.abs(gray[row + x + 2] - gray[row + x]);
      if (ag < gl || ag < gr) continue;
      const dir = Math.sign(g);
      let l = x;
      while (l > 0 && (gray[row + l] - gray[row + l - 1]) * dir > 0) l--;
      let r = x;
      while (r < width - 1 && (gray[row + r + 1] - gray[row + r]) * dir > 0) r++;
      if (Math.abs(gray[row + r] - gray[row + l]) < EDGE_MIN_CONTRAST) continue;
      widths.push(r - l);
      if (labelMask && x < tilesX * TILE && labelMask[Math.floor(y / TILE) * tilesX + Math.floor(x / TILE)]) labelWidths.push(r - l);
      x = r;
    }
  }
  widths.sort((a, b) => a - b);
  const megapixels = (width * height) / 1e6;
  return {
    edgeCount: widths.length,
    edgePerMpx: megapixels > 0 ? round(widths.length / megapixels) : null,
    edgeWidthP50: quantile(widths, 0.5),
    edgeWidthMean: widths.length ? round2(widths.reduce((a, b) => a + b, 0) / widths.length) : null,
    labelEdgeWidthMean: labelWidths.length ? round2(labelWidths.reduce((a, b) => a + b, 0) / labelWidths.length) : null,
  };
}

// gray: 長さ width*height の明るさ（0〜255）配列。ブラウザに依存しない純粋関数（テスト対象）
export function computeSharpness(gray, width, height) {
  const { labelMask, tilesX, ...lap } = laplacianStats(gray, width, height);
  const edges = edgeStats(gray, width, height, labelMask, tilesX);
  return {
    v: SHARPNESS_VERSION,
    w: width,
    h: height,
    ...edges,
    ...lap,
    // 文字の大きさに対するにじみ（にじみの幅 ÷ 画の間隔）。大きいほど、文字の大きさの割にぼけている
    labelEdgeRel: edges.labelEdgeWidthMean != null && lap.labelPitchP50 > 0
      ? round2(edges.labelEdgeWidthMean / lap.labelPitchP50) : null,
  };
}

// RGBA（canvas の getImageData）を明るさに変換する
export function rgbaToGray(data, width, height) {
  const gray = new Uint8ClampedArray(width * height);
  for (let i = 0, j = 0; i < gray.length; i++, j += 4) {
    gray[i] = 0.299 * data[j] + 0.587 * data[j + 1] + 0.114 * data[j + 2];
  }
  return gray;
}

// 計測する画像の長辺の上限。撮影画像は解析 API 向けに長辺 1280px へ縮小済み（App.js の
// compressImage）で、しきい値（lib/dict/imageQuality.js）もこの大きさで決めた。100万画素あたりの
// エッジ数は画像の大きさで変わるため、これより大きい画像が渡っても同じ尺度で測れるよう縮小する
export const MEASURE_MAX_SIZE = 1280;

// data URL の画像を計測する（ブラウザ専用）。失敗しても解析の邪魔をしないよう null を返す
export function measureImageSharpness(dataUrl) {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        try {
          const scale = Math.min(1, MEASURE_MAX_SIZE / Math.max(img.width, img.height));
          const w = Math.max(1, Math.round(img.width * scale));
          const h = Math.max(1, Math.round(img.height * scale));
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(img, 0, 0, w, h);
          const { data } = ctx.getImageData(0, 0, w, h);
          resolve(computeSharpness(rgbaToGray(data, w, h), w, h));
        } catch {
          resolve(null);
        } finally {
          // iOS Safari はキャンバスのメモリを全体で上限管理しており、使い終えたキャンバスが
          // すぐには解放されない。一括撮影やリグレッションテストで何十枚も続けて計測すると
          // 上限に達して getContext が null を返す（以降の計測がすべて失敗する）ため、
          // 大きさを 0 にして明示的に手放す
          canvas.width = 0;
          canvas.height = 0;
        }
      };
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    } catch {
      resolve(null);
    }
  });
}

// 1 枚あたりの計測の上限時間。計測は解析の付随情報なので、端末が遅くても解析を待たせない
export const MEASURE_TIMEOUT_MS = 3000;

// 解析 API に送る画像（data URL または base64 本体）をまとめて計測する。
// どれだけ失敗しても例外は投げず、計測できなかった画像は null にして並べて返す
export async function measureImagesSharpness(images, timeoutMs = MEASURE_TIMEOUT_MS) {
  const list = Array.isArray(images) ? images : [];
  return Promise.all(list.map((src) => {
    if (typeof src !== 'string' || src.length === 0) return Promise.resolve(null);
    const dataUrl = src.startsWith('data:') ? src : `data:image/jpeg;base64,${src}`;
    let timer;
    const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve(null), timeoutMs); });
    return Promise.race([measureImageSharpness(dataUrl), timeout]).finally(() => clearTimeout(timer));
  }));
}

// 解析結果・検査履歴に「画像不鮮明」の印を付けるか。判断はサーバー（api/_imageQuality.js の
// withBlurryFlag）が計測値から行い、image_quality.blurry に入れて返す・保存する
export const BLURRY_IMAGE_LABEL = '画像不鮮明';
export const BLURRY_IMAGE_NOTE = '画像がぼけているか、文字が小さく写っている可能性があります。AIの読み取りが誤っている恐れがあります。';
// 検査結果画面（照合履歴のカード・詳細）で「画像不鮮明」の検査に添える撮り直しの案内。
// food-label-assist（「ラベルに近づき、…」）とは文言を変えている。PM は中身とラベルを照合するため
// 商品全体が写っている必要があり、ラベルに近づくよう促すと照合に要る中身が写らなくなる
export const BLURRY_RETAKE_MESSAGE = '商品全体とラベルが写るようにして、ラベルにピントを合わせて再度撮影してください。';
export function isBlurryImage(imageQuality) {
  return !!(imageQuality && typeof imageQuality === 'object' && imageQuality.blurry === true);
}
// PDF・印刷用の印（判定の表示の横に置く）
export function blurryImageBadgeHtml(imageQuality) {
  if (!isBlurryImage(imageQuality)) return '';
  return `<span style="display:inline-block;padding:1px 6px;border-radius:3px;font-size:9px;font-weight:800;margin-left:4px;background:#f59e0b;color:#fff">${BLURRY_IMAGE_LABEL}</span>`;
}
